import axios, { type AxiosRequestConfig } from 'axios';
import { GRAPHQL_CONSISTENCY_TOKENS_ENABLED, RELEASE_VERSION } from '../constants/environment';
import { IMPERSONATED_KEY } from '../constants/sessionStorage';
import {
    CONSISTENCY_TOKEN_HEADER,
    getConsistencyTokenHeaderValue,
    syncConsistencyTokensFromHeaders,
} from '../helpers/graphqlConsistency';
import getCookie from './getCookie';

/**
 * Body of a request as a key-value map.
 */
export interface RequestBody {
    [key: string]: any;
}

/**
 * Query-string params of a request as a
 * key-value map.
 */
export interface RequestParams {
    [key: string]: any;
}

/**
 * Request HTTP methods.
 */
export enum HTTPMethod {
    Get = 'GET',
    Post = 'POST',
    Put = 'PUT',
    Patch = 'PATCH',
    Delete = 'DELETE',
}

/**
 * HTTP headers as a key-value map.
 */
interface HTTPHeaders {
    [key: string]: any;
}

const toResponseHeaders = (headers: unknown): Headers => {
    const responseHeaders = new Headers();
    if (!headers || typeof headers !== 'object') {
        return responseHeaders;
    }

    for (const [name, value] of Object.entries(headers)) {
        if (Array.isArray(value)) {
            responseHeaders.set(name, value.join(', '));
            continue;
        }
        if (typeof value === 'string') {
            responseHeaders.set(name, value);
        }
    }
    return responseHeaders;
};

export interface FieldErrors {
    [key: string]: string[] | undefined;
}

export class BadRequestError extends Error {
    fieldErrors: FieldErrors;
    errorCode?: string;

    constructor(message: string, fieldErrors: FieldErrors, errorCode?: string) {
        super(message);
        this.fieldErrors = fieldErrors;
        this.errorCode = errorCode;
    }
}

export class UnauthorizedError extends Error {
    code: string;

    constructor(message: string, code?: string) {
        const msg = message ?? 'You must be logged in to perform this request.';
        super(msg);
        this.code = code ?? '';
    }
}

export type MFAMethod = 'sms' | 'totp' | 'recovery_code' | 'passkey';

export class MfaRequiredError extends Error {
    token: string;
    expiresInSeconds: number;
    methods: MFAMethod[];

    constructor({
        token,
        expiresInSeconds,
        methods,
    }: {
        token: string;
        expiresInSeconds: number;
        methods: MFAMethod[];
    }) {
        super('Multi-factor authentication is required.');
        this.token = token;
        this.expiresInSeconds = expiresInSeconds;
        this.methods = methods;
    }
}

export class MfaSetupRequiredError extends Error {
    policy: unknown;

    constructor({ message, policy }: { message?: string; policy?: unknown }) {
        super(message ?? 'Multi Factor Authentication (MFA) setup is required.');
        this.policy = policy;
    }
}

export class ForbiddenError extends Error {
    code: string;
    constructor(message: string, code?: string) {
        const msg = message ?? 'You are not authorized to perform this request.';
        super(msg);
        this.code = code ?? '';
    }
}

export class TooManyRequestsError extends Error {
    constructor(message?: string) {
        const msg = message ?? 'Too many requests. Please try again shortly.';
        super(msg);
    }
}

export class NotFoundError extends Error {
    constructor() {
        super('The requested resource cannot be found.');
    }
}

export class GoneError extends Error {
    constructor() {
        super('This resource is no longer available.');
    }
}

export class UnprocessableError extends Error {
    constructor(message: string) {
        const msg = message ?? 'The server is unable to process your request.';
        super(msg);
    }
}

export class InternalServerError extends Error {
    constructor() {
        super('Something went wrong.');
    }
}

export interface UploadedFile {
    url: string;
    key: string;
    fileName: string;
    fileType: File['type'];
    fileSize: File['size'];
    lastModified: File['lastModified'];
}

export interface UploadFileOptions {
    uploadPurpose?: string;
}

export const getUploadedFileReference = (uploadedFile: Pick<UploadedFile, 'key' | 'url'>) =>
    uploadedFile.key || uploadedFile.url;

/**
 * ResQ API client that handles URL formatting,
 * auth and errors.
 */
export default class APIClient {
    /** API host URL. */
    protected host: string;
    /** CSRF token cookie name, see https://docs.djangoproject.com/en/2.1/ref/csrf/. */
    protected csrfTokenCookieName: string;

    /**
     * Creates a new instance of the API client.
     * @param host - API host URL.
     * @param csrfTokenCookieName - CSRF token cookie name.
     */
    constructor(host: string, csrfTokenCookieName = 'csrftoken') {
        this.host = host;
        this.csrfTokenCookieName = csrfTokenCookieName;
    }

    /**
     * Makes a GET request.
     * @param path - URL path for the endpoint relative to the host.
     * @param params - Query-string params as a key-value map.
     * @param headers - Optional headers for the request.
     */
    get(path: string, params?: RequestParams, headers?: HTTPHeaders) {
        return this.request(HTTPMethod.Get, path, undefined, params, headers);
    }

    /**
     * Makes a POST request.
     * @param path - URL path for the endpoint relative to the host.
     * @param body - Body as a key-value map.
     * @param params - Query-string params as a key-value map.
     * @param headers - Optional headers for the request.
     */
    post(path: string, body?: RequestBody, params?: RequestParams, headers?: HTTPHeaders) {
        return this.request(HTTPMethod.Post, path, body, params, headers);
    }

    /**
     * Makes a PUT request.
     * @param path - URL path for the endpoint relative to the host.
     * @param body - Body as a key-value map.
     * @param params - Query-string params as a key-value map.
     * @param configs - Optional configs for the request.
     */
    put(path: string, body?: RequestBody, params?: RequestParams) {
        return this.request(HTTPMethod.Put, path, body, params);
    }

    /**
     * Makes a PATCH request.
     * @param path - URL path for the endpoint relative to the host.
     * @param body - Body as a key-value map.
     * @param params - Query-string params as a key-value map.
     */
    patch(path: string, body?: RequestBody, params?: RequestParams) {
        return this.request(HTTPMethod.Patch, path, body, params);
    }

    /**
     * Makes a DELETE request.
     * @param path - URL path for the endpoint relative to the host.
     * @param params - Query-string params as a key-value map.
     * @param body - Optional body as a key-value map.
     * @param configs - Optional configs for the request.
     */
    delete(path: string, params?: RequestParams, body?: RequestBody) {
        return this.request(HTTPMethod.Delete, path, body, params);
    }
    async uploadFile(
        file: File,
        endpoint?: string,
        headers?: HTTPHeaders,
        options?: UploadFileOptions,
    ): Promise<UploadedFile> {
        const { url, fields, getUrl, relativeKey } = await this.post(
            endpoint ?? '/generate-file-upload-presigned-url/',
            {
                fileName: file.name,
                contentType: file.type,
                uploadPurpose: options?.uploadPurpose,
            },
            undefined,
            headers,
        );
        const formData = new FormData();
        Object.keys(fields).forEach((key) => {
            formData.append(key, fields[key]);
        });
        formData.append('file', file);
        await axios.post(url, formData);
        return {
            url: getUrl as string,
            key: relativeKey as string,
            fileName: file.name,
            fileType: file.type || 'application/octet-stream',
            fileSize: file.size,
            lastModified: file.lastModified,
        };
    }

    /**
     * Uploads multiple files to S3 by using generated signed POST URLs.
     * @param files - Array of files to upload
     * @param endpoint - Optional endpoint for generating presigned URLs
     * @returns Array of uploaded file information
     */
    async uploadMultipleFiles(
        files: File[],
        endpoint?: string,
        options?: UploadFileOptions,
    ): Promise<UploadedFile[]> {
        const uploadPromises = files.map((file) => {
            return this.uploadFile(file, endpoint, undefined, options);
        });

        return await Promise.all(uploadPromises);
    }

    /**
     * Makes a HTTP request.
     * @param method - HTTP method.
     * @param path - URL path for the endpoint relative to the host.
     * @param body - Body as a key-value map.
     * @param params - Query-string params as a key-value map.
     * @param headers - Optional headers for the request.
     */
    protected async request(
        method: HTTPMethod,
        path: string,
        body?: RequestBody,
        params?: RequestParams,
        headers?: HTTPHeaders,
    ) {
        // Build URL
        const url = `${this.host}${path}`;

        // Build request headers
        const defaultHeaders: HTTPHeaders = {
            'Content-Type': 'application/json',
            'X-Client-Platform': 'web',
        };
        if (RELEASE_VERSION) {
            defaultHeaders['X-Client-Version'] = RELEASE_VERSION;
        }
        const csrfToken = getCookie(this.csrfTokenCookieName);
        if (csrfToken) {
            defaultHeaders['X-CSRFToken'] = csrfToken;
        }
        const impersonated = sessionStorage.getItem(IMPERSONATED_KEY);
        if (impersonated) {
            defaultHeaders['X-Impersonate'] = impersonated;
        }
        if (GRAPHQL_CONSISTENCY_TOKENS_ENABLED) {
            const consistencyTokens = getConsistencyTokenHeaderValue();
            if (consistencyTokens) {
                defaultHeaders[CONSISTENCY_TOKEN_HEADER] = consistencyTokens;
            }
        }

        // Merge default headers with passed headers
        const mergedHeaders = { ...defaultHeaders, ...headers };

        // Make request with axios
        try {
            const axiosConfig: AxiosRequestConfig = {
                data: body,
                headers: mergedHeaders,
                method,
                params,
                url,
                withCredentials: true,
            };
            const response = await axios.request(axiosConfig);
            if (GRAPHQL_CONSISTENCY_TOKENS_ENABLED) {
                try {
                    syncConsistencyTokensFromHeaders(toResponseHeaders(response.headers));
                } catch (error) {
                    console.warn('Failed to sync response consistency tokens.', error);
                }
            }
            return response.data;
        } catch (err: any) {
            console.log('error!', err); // do not remove - needed for e2e debugging
            const response = err.response;
            if (!response) {
                throw err;
            }
            // TODO: Implement better error handling
            // Not urgent since we mostly use Apollo
            switch (response.status) {
                case 400:
                    throw new BadRequestError(
                        response.data.error || response.data.detail || 'BAD_REQUEST',
                        (response.data || {}).fieldErrors || {},
                        response.data.errorCode || response.data.error_code,
                    );
                case 401:
                    if (response.data?.mfa_required === true && response.data?.mfa_token) {
                        throw new MfaRequiredError({
                            token: response.data.mfa_token,
                            expiresInSeconds: response.data.expires_in_seconds ?? 0,
                            methods: response.data.methods ?? ['totp'],
                        });
                    }
                    throw new UnauthorizedError(response.data.detail, response.data.code);
                case 403:
                    if (response.data?.mfa_setup_required === true) {
                        throw new MfaSetupRequiredError({
                            message: response.data.detail,
                            policy: response.data.policy,
                        });
                    }
                    throw new ForbiddenError(response.data.detail, response.data.code);
                case 429:
                    throw new TooManyRequestsError(
                        response.data.error || response.data.detail || undefined,
                    );
                case 404:
                    throw new NotFoundError();
                case 410:
                    throw new GoneError();
                case 422:
                    throw new UnprocessableError(response.data.detail);
                default:
                    throw new InternalServerError();
            }
        }
    }
}
